import React, { useState } from 'react';
import './CaseEvidenceFrameView.css';

/**
 * Helper to render a list of objects as a small key/value table.
 */
function ObjectTable({ items, columns }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <div className="cef-empty">— empty —</div>;
  }
  const cols = columns || Object.keys(items[0] || {}).slice(0, 6);
  return (
    <div className="cef-scroll">
      <table className="cef-table">
        <thead>
          <tr>
            {cols.map(c => <th key={c}>{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => (
            <tr key={i}>
              {cols.map(c => {
                const v = row[c];
                let display;
                if (v === null || v === undefined) display = '—';
                else if (typeof v === 'object') display = JSON.stringify(v);
                else display = String(v);
                return <td key={c} title={display}>{display}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * CefSection -- collapsible section card.
 */
function CefSection({ title, count, defaultOpen, children, accent }) {
  const [open, setOpen] = useState(defaultOpen ?? (count > 0));
  return (
    <div className={`cef-section ${accent || ''}`}>
      <button
        type="button"
        className="cef-section-header"
        onClick={() => setOpen(!open)}
      >
        <span className="cef-section-title">{title}</span>
        <span className="cef-section-count">{count}</span>
        <span className="cef-section-toggle">{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="cef-section-body">{children}</div>}
    </div>
  );
}

/**
 * CaseEvidenceFrameView
 *
 * Renders the structured CaseEvidenceFrame returned by the
 * `case_extractor` node. Each section card collapses independently.
 *
 * @param {Object} props
 * @param {import('../../../types/categoricalReasoner').CaseEvidenceFrame} props.frame
 */
function CaseEvidenceFrameView({ frame }) {
  if (!frame || typeof frame !== 'object') {
    return <div className="cef-empty">No CaseEvidenceFrame produced.</div>;
  }
  const warnings = Array.isArray(frame.extractor_warnings) ? frame.extractor_warnings : [];
  const masterCustomer = frame.master_customer || {};
  const entities = Array.isArray(frame.entities) ? frame.entities : [];
  const relationships = Array.isArray(frame.relationships) ? frame.relationships : [];
  const transactions = Array.isArray(frame.transactions) ? frame.transactions : [];
  const screening = frame.screening || {};
  const kyc = frame.kyc || {};
  const inquiries = Array.isArray(frame.regulatory_inquiries) ? frame.regulatory_inquiries : [];
  const adverseMedia = Array.isArray(frame.adverse_media) ? frame.adverse_media : [];
  const auditorNotes = Array.isArray(frame.auditor_notes) ? frame.auditor_notes : [];
  const alerts = Array.isArray(frame.alerts) ? frame.alerts : [];

  return (
    <div className="case-evidence-frame">
      {(frame.case_id || frame.domain) && (
        <div className="cef-summary">
          {frame.case_id && <span>case_id: <strong>{frame.case_id}</strong></span>}
          {frame.domain && <span>domain: <strong>{frame.domain}</strong></span>}
        </div>
      )}

      {warnings.length > 0 && (
        <div className="cef-warnings">
          <div className="cef-warnings-title">Extractor warnings</div>
          <ul>
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      <CefSection title="Master Customer" count={Object.keys(masterCustomer).length} defaultOpen>
        {Object.keys(masterCustomer).length > 0 ? (
          <table className="cef-kv-table">
            <tbody>
              {Object.entries(masterCustomer).map(([k, v]) => (
                <tr key={k}>
                  <th>{k}</th>
                  <td>{v === null || v === undefined ? '—' : (typeof v === 'object' ? JSON.stringify(v) : String(v))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <div className="cef-empty">— not extracted —</div>}
      </CefSection>

      <CefSection title="Entities" count={entities.length}>
        <ObjectTable items={entities} columns={['entity_id', 'name', 'type', 'country', 'address']} />
      </CefSection>

      <CefSection title="Relationships" count={relationships.length}>
        <ObjectTable items={relationships} columns={['from_entity', 'type', 'to_entity', 'evidence_quote']} />
      </CefSection>

      <CefSection title="Transactions" count={transactions.length}>
        <ObjectTable items={transactions} columns={['txn_id', 'date', 'amount_usd', 'currency', 'originator', 'beneficiary', 'counterparty_country']} />
      </CefSection>

      <CefSection title="Screening" count={Object.keys(screening).length}>
        <pre className="cef-pre">{JSON.stringify(screening, null, 2)}</pre>
      </CefSection>

      <CefSection title="KYC" count={Object.keys(kyc).length}>
        <pre className="cef-pre">{JSON.stringify(kyc, null, 2)}</pre>
      </CefSection>

      <CefSection title="Regulatory Inquiries" count={inquiries.length}>
        <ObjectTable items={inquiries} columns={['agency', 'date', 'request_type', 'evidence_quote']} />
      </CefSection>

      <CefSection title="Adverse Media" count={adverseMedia.length}>
        <ObjectTable items={adverseMedia} columns={['date', 'source', 'subject', 'summary']} />
      </CefSection>

      <CefSection title="Auditor Notes" count={auditorNotes.length}>
        <ObjectTable items={auditorNotes} columns={['date', 'author', 'summary']} />
      </CefSection>

      <CefSection title="Alerts" count={alerts.length}>
        <ObjectTable items={alerts} columns={['alert_id', 'date', 'rule', 'severity', 'summary']} />
      </CefSection>

      <details className="cef-raw">
        <summary>Raw JSON</summary>
        <pre className="cef-pre">{JSON.stringify(frame, null, 2)}</pre>
      </details>
    </div>
  );
}

export default CaseEvidenceFrameView;
